import { IBigPlank } from '@/partTypes'
import { MixPaibanPlans } from '@/store/modules/IMaterialListType/type'
import { message } from 'ant-design-vue'

/**
 * 低版本 / 非 HTTPS 等环境下用 textarea + execCommand 复制（保留换行）。
 * iOS、部分 WebKit 需 readonly、fixed 占位、focus + setSelectionRange。
 */
function copyTextWithExecCommand(text: string): boolean {
  if (
    typeof document === 'undefined' ||
    typeof document.execCommand !== 'function'
  ) {
    return false
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'readonly')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  textarea.style.opacity = '0'
  textarea.style.pointerEvents = 'none'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  const len = textarea.value.length
  try {
    textarea.setSelectionRange(0, len)
  } catch {
    // 极少数环境不支持 setSelectionRange，仍尝试 copy
  }
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  } finally {
    document.body.removeChild(textarea)
  }
  return ok
}

/**
 * 用于文本复制（优先 Clipboard API，失败或无能力时回退 execCommand）
 */
export const copyText = (
  text: string,
  successMsg = '复制成功',
  errMsg = '复制失败'
) => {
  const onSuccess = () => message.success(successMsg)
  const onError = () => message.error(errMsg)

  const tryLegacy = () => {
    try {
      return copyTextWithExecCommand(text)
    } catch (err) {
      console.log(err)
      return false
    }
  }

  const clipboard =
    typeof navigator !== 'undefined' ? navigator.clipboard : undefined
  if (clipboard && typeof clipboard.writeText === 'function') {
    clipboard
      .writeText(text)
      .then(onSuccess)
      .catch((err) => {
        console.log(err)
        if (tryLegacy()) {
          onSuccess()
        } else {
          onError()
        }
      })
    return
  }

  if (tryLegacy()) {
    onSuccess()
  } else {
    onError()
  }
}

/**
 * @description 数组根据步数进行内部值的移动
 * @param arr 数组
 * @param steps 移动步数
 * @returns
 */
export function rotateArray(arr: any[], steps: number) {
  if (!Array.isArray(arr)) arr = []
  if (typeof steps !== 'number') steps = 0
  const length = arr.length
  if (length === 0) return arr // 空数组直接返回
  // 计算实际移动步数，考虑负数和数组长度
  const actualSteps = ((steps % length) + length) % length
  return arr.slice(-actualSteps).concat(arr.slice(0, -actualSteps))
}

/**
 * @description 去除数组中相同的元素
 * @param arr1
 * @param arr2
 * @returns
 */
export function removeRepeat(arr1: any[], arr2: any[]) {
  return arr1.filter((it) => !arr2.includes(it))
}

/**
 * @description 判断两个对象是否匹配,符合原片的宽松匹配机制
 * @param target 目标对象
 * @param filter 过滤对象
 * @returns 是否匹配
 */
export function isSomePlankMatch(
  target: { [k: string]: number | string | null },
  filter: { [k: string]: number | string | null }
) {
  return Object.entries(filter).every(([key, value]) => {
    if (value == null) return true // 没填则忽略
    return target[key] === value
  })
}

export function checkBigPlankIsSpecialShape(bigPlank: IBigPlank) {
  const surplusInfo = bigPlank.surplusInfo
  return (
    surplusInfo &&
    (!surplusInfo.isNotSurplus || surplusInfo.is_plus) &&
    ['2', 'specialShape'].includes(String(surplusInfo.shape)) &&
    surplusInfo.path &&
    surplusInfo.path.length > 7
  )
}

// 用作匹配混排方案
export function matchMixPaibanPlan(
  plan: MixPaibanPlans,
  payload: { matCode: string; texture: string }
): boolean {
  const { matCode, texture } = payload
  return !!(
    plan.isSelected &&
    plan.matCode === matCode.replace('高光_', '') &&
    plan.allColors?.includes(texture)
  )
}

/**
 * @description 获取文本中花括号内的内容(包括花括号)
 * @param text
 * @returns
 */
export function getCurlyBracketText(text: string) {
  return text.match(/{([^}]+)}/g) ?? []
}

/**
 * @description 去除 base64 URL 头信息，只保留 base64 数据部分
 */
export function removeBase64Head(base64: string) {
  if (typeof base64 !== 'string') return base64
  return base64.replace(/^data:.+?;base64,/, '')
}

/**
 * @description 递增序号，支持前导零 001 / 1 -> 002 / 2
 * @param value 当前值
 * @param step 递增步数
 * @returns 递增后的字符串
 */
export function incrementStartNum(
  value: string | number | undefined,
  step = 1
): string {
  const raw = value ?? 1
  const rawStr = String(raw)
  const isNumericStr = /^\d+$/.test(rawStr)
  const width = isNumericStr ? rawStr.length : 0
  const base = Number(rawStr)
  const next = (Number.isFinite(base) ? base : 0) + step
  const nextStr = String(next)
  if (width > 0 && nextStr.length < width) {
    return nextStr.padStart(width, '0')
  }
  return nextStr
}
